// Pure pointer / hit-test helpers for the minimap canvas. Split out of
// useMinimapInteraction.ts to keep that hook under the repo's 400-line rule.

import { type PreparedBoard, tileAt } from './minimapBoard.js';
import { axialToUnit, unitToAxial } from './minimapGeometry.js';
import { type Viewport, screenToUnit } from './minimapViewport.js';
import type { MinimapTile } from './types.js';

/** What a completed drag gesture does: move the camera (primary) or pan the viewport. */
export type DragMode = 'camera' | 'pan';

/** Shift+primary and middle-button drags pan the minimap viewport; a plain primary
 * drag moves the camera when the App listens for it. */
export function dragMode(
  e: { shiftKey: boolean; button: number },
  cameraAvailable: boolean,
): DragMode {
  if (e.button === 1 || e.shiftKey) return 'pan';
  return cameraAvailable ? 'camera' : 'pan';
}

export interface HoverState {
  readonly q: number;
  readonly r: number;
  /** Pointer position inside the canvas (CSS px) for tooltip placement. */
  readonly x: number;
  readonly y: number;
  readonly touch: boolean;
}

export interface FocusState {
  readonly q: number;
  readonly r: number;
}

export interface PointerRec {
  x: number;
  y: number;
}

export interface GestureState {
  dragged: boolean;
  startX: number;
  startY: number;
  pinchDist: number;
  /** Centre of the two pointers last frame; the pan delta is measured against it. */
  pinchCentre: { x: number; y: number } | null;
  multi: boolean;
  /** Decided at threshold crossing: which drag behaviour this gesture is. */
  mode: DragMode;
  /** Pointer button that started the gesture (1 = middle = always pan). */
  button: number;
  /** Unit-space point under the pointer at drag start (camera mode). */
  startUnit: { x: number; y: number } | null;
}

/** Tile under a canvas-local point, or undefined off the board. */
export function hitTest(
  board: PreparedBoard,
  vp: Viewport,
  sx: number,
  sy: number,
): { q: number; r: number; tile: MinimapTile } | undefined {
  const u = screenToUnit(vp, sx, sy);
  const { q, r } = unitToAxial(u.x, u.y);
  const tile = tileAt(board, q, r);
  return tile ? { q, r, tile } : undefined;
}

/** The tile nearest the board centre (the keyboard focus starts there). */
export function centreTile(board: PreparedBoard): { q: number; r: number } | null {
  const cx = (board.bounds.minX + board.bounds.maxX) / 2;
  const cy = (board.bounds.minY + board.bounds.maxY) / 2;
  const { q, r } = unitToAxial(cx, cy);
  if (tileAt(board, q, r)) return { q, r };
  let best: { q: number; r: number } | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const t of board.tiles) {
    const u = axialToUnit(t.q, t.r);
    const d = (u.x - cx) ** 2 + (u.y - cy) ** 2;
    if (d < bestD) {
      bestD = d;
      best = { q: t.q, r: t.r };
    }
  }
  return best;
}
